import { useRef, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from 'semantic-ui-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  List,
} from 'ckeditor5';
import './activity.scss';

export const TOOLBAR_CONFIG = {
  plugins: [Essentials, Bold, Italic, List, Paragraph],
  toolbar: ['bold', 'italic', 'bulletedList', 'numberedList'],
};

/**
 * TAK-2086: saves one "Show in report" choice. Only the changed key is sent —
 * the server merges it into the activity's stored inclusions and returns the
 * merged set — so toggles on different cards can't overwrite each other. The
 * merged set is broadcast so the activity page and any open Report Builder
 * tab stay in step. Returns the merged inclusions, or null if the save failed.
 */
export async function saveReportInclusion({ activityId, key, value }) {
  try {
    const { data } = await axios.put(
      `/project/${activityId}/report-inclusions`,
      {
        reportInclusions: { [key]: value },
      }
    );
    const merged = data?.reportInclusions || { [key]: value };
    const ch = new BroadcastChannel('takso_activity');
    ch.postMessage({
      type: 'reportInclusions',
      activityId,
      reportInclusions: merged,
    });
    ch.close();
    return merged;
  } catch (e) {
    console.error('Failed to save report inclusions', e);
    return null;
  }
}

// "Show in report: Yes / No" toggle
export function IncludeInReportToggle({ included, onToggle, disabled }) {
  const { t } = useTranslation();

  return (
    <div className="checkbox-container-include-in-report">
      <div className="include-in-report-label">
        {t('activity.report.show_in_report')}
      </div>
      <div className="button-include">
        <Button.Group className="btn-include-in-report">
          <Button
            type="button"
            active={included}
            disabled={disabled}
            onClick={() => !disabled && onToggle(true)}
          >
            {t('actions.yes')}
          </Button>
          <Button
            type="button"
            active={!included}
            disabled={disabled}
            onClick={() => !disabled && onToggle(false)}
          >
            {t('actions.no')}
          </Button>
        </Button.Group>
      </div>
    </div>
  );
}

// Wraps a CKEditor instance with a label and optional help message.
// Saves to the backend on editor blur via the internal view event — consistent
// with EvaluationMethodology and required because CKEditor replaces the native
// textarea and does not expose a DOM onBlur.
// When `labelToggle` is provided it renders in the label row (label left,
// toggle right) so the editor below remains full width.
export function CKField({
  label,
  placeholder,
  initialData,
  fieldName,
  putActivity,
  disabled,
  helpMessage,
  labelToggle,
}) {
  const initialDataRef = useRef(null);
  if (initialDataRef.current === null) {
    initialDataRef.current = initialData || '';
  }
  const putActivityRef = useRef(putActivity);
  putActivityRef.current = putActivity;
  const editorDataRef = useRef(initialDataRef.current);
  const fieldNameRef = useRef(fieldName);
  fieldNameRef.current = fieldName;

  // Save is normally driven by CKEditor's internal blur event, but switching
  // tabs unmounts this component directly without ever firing that blur,
  // silently discarding the pending edit. Flush on unmount as a safety net,
  // mirroring the blur condition exactly.
  useEffect(() => {
    return () => {
      if (editorDataRef.current !== initialDataRef.current) {
        putActivityRef.current('projectNotes', {
          [fieldNameRef.current]: editorDataRef.current,
        });
      }
    };
  }, []);

  return (
    <div style={{ marginBottom: 16 }}>
      {(label || labelToggle) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <div style={{ fontWeight: 600 }}>{label}</div>
          {labelToggle}
        </div>
      )}

      <div className="report-ck-field" style={{ '--ck-border-radius': '8px' }}>
        <CKEditor
          editor={ClassicEditor}
          config={{ ...TOOLBAR_CONFIG, placeholder }}
          data={initialDataRef.current}
          disabled={disabled}
          onChange={(event, editor) => {
            editorDataRef.current = editor.getData();
          }}
          onReady={(editor) => {
            editor.editing.view.document.on('blur', () => {
              putActivityRef.current('projectNotes', {
                [fieldName]: editorDataRef.current,
              });
            });
          }}
        />
      </div>
      {helpMessage && (
        <small style={{ color: '#6b7280', display: 'block', marginTop: 4 }}>
          {helpMessage}
        </small>
      )}
    </div>
  );
}
